import type { ReportGroupRow, ReportsOverviewPayload } from "@noverik/contracts";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Download,
  RotateCcw,
  TicketCheck,
  UserCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest, ApiRequestError } from "../lib/api";

type GroupKey = "departments" | "topics" | "agents";

const groupLabels: Record<GroupKey, string> = {
  departments: "Departamentos",
  topics: "Tópicos",
  agents: "Atendentes",
};

function csvCell(value: string | number | null): string {
  const text = value === null ? "" : String(value);
  return '"' + text.replaceAll('"', '""') + '"';
}

export function ReportsPage() {
  const [days, setDays] = useState(30);
  const [group, setGroup] = useState<GroupKey>("departments");
  const [data, setData] = useState<ReportsOverviewPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await apiRequest<ReportsOverviewPayload>(
        "/agent/reports/overview?days=" + days,
      );
      setData(payload);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível carregar os relatórios.",
      );
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () => data?.groups[group] ?? [],
    [data, group],
  );

  const exportCsv = () => {
    if (!data) return;

    const header = [
      groupLabels[group],
      "Abertos",
      "Atribuídos",
      "Atrasados",
      "Fechados",
      "Reabertos",
      "Excluídos",
      "Tempo de serviço (h)",
      "Tempo de resposta (h)",
    ];

    const body = rows.map((row) => [
      row.label,
      row.opened,
      row.assigned,
      row.overdue,
      row.closed,
      row.reopened,
      row.deleted,
      row.serviceTimeHours,
      row.responseTimeHours,
    ]);

    const csv = [header, ...body]
      .map((line) => line.map(csvCell).join(";"))
      .join("\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = "noverik-relatorio-" + group + "-" + days + "d.csv";
    link.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="page reports-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">OPERAÇÃO</span>
          <h1>Relatórios</h1>
          <p>Indicadores nativos do osTicket reorganizados para acompanhamento da operação.</p>
        </div>

        <div className="report-actions">
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
            <option value={365}>Últimos 365 dias</option>
          </select>
          <button className="button secondary" onClick={exportCsv} disabled={!data || rows.length === 0}>
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {loading && <div className="data-message">Calculando indicadores...</div>}
      {error && <div className="data-message error">{error}</div>}

      {!loading && !error && data && (
        <>
          <section className="report-summary">
            <div className="report-kpi">
              <span className="meta-icon blue"><TicketCheck size={19} /></span>
              <div><strong>{data.summary.opened}</strong><span>Abertos no período</span></div>
            </div>
            <div className="report-kpi">
              <span className="meta-icon purple"><UserCheck size={19} /></span>
              <div><strong>{data.summary.assigned}</strong><span>Atribuições</span></div>
            </div>
            <div className="report-kpi danger">
              <span className="meta-icon amber"><AlertTriangle size={19} /></span>
              <div><strong>{data.summary.overdue}</strong><span>Eventos de atraso</span></div>
            </div>
            <div className="report-kpi">
              <span className="meta-icon green"><CheckCircle2 size={19} /></span>
              <div><strong>{data.summary.closed}</strong><span>Fechamentos</span></div>
            </div>
            <div className="report-kpi">
              <span className="meta-icon blue"><RotateCcw size={19} /></span>
              <div><strong>{data.summary.reopened}</strong><span>Reaberturas</span></div>
            </div>
          </section>

          <section className="report-card">
            <div className="report-tabs">
              {(Object.keys(groupLabels) as GroupKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={group === key ? "active" : ""}
                  onClick={() => setGroup(key)}
                >
                  {groupLabels[key]}
                </button>
              ))}
            </div>

            <div className="report-context">
              <Activity size={16} />
              <span>
                Janela de {data.periodDays} dias. Tempos apresentados em horas e calculados pelo mecanismo de estatísticas do osTicket.
              </span>
            </div>

            <div className="ticket-table-wrap">
              <table className="ticket-table report-table">
                <thead>
                  <tr>
                    <th>{groupLabels[group]}</th>
                    <th>Abertos</th>
                    <th>Atribuídos</th>
                    <th>Atrasados</th>
                    <th>Fechados</th>
                    <th>Reabertos</th>
                    <th>Serviço</th>
                    <th>Resposta</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: ReportGroupRow) => (
                    <tr key={row.kind + "-" + row.label}>
                      <td><strong>{row.label || "Sem identificação"}</strong></td>
                      <td>{row.opened}</td>
                      <td>{row.assigned}</td>
                      <td>{row.overdue}</td>
                      <td>{row.closed}</td>
                      <td>{row.reopened}</td>
                      <td>{row.serviceTimeHours === null ? "—" : row.serviceTimeHours.toLocaleString("pt-BR") + " h"}</td>
                      <td>{row.responseTimeHours === null ? "—" : row.responseTimeHours.toLocaleString("pt-BR") + " h"}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <div className="table-empty">
                          <strong>Sem dados para este período.</strong>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
